import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { X } from 'lucide-react';
import { type Ref } from 'react';
import { useIntl } from 'react-intl';

import { CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS } from '../clinician-max-upgrade-theme';
import { usageNudgeCardMessages } from './usage-nudge-card-messages';

export type UsageNudgeCardProps = {
  title: string;
  body: string;
  /** Usage limits article, linked after the body. */
  learnMoreHref?: string;
  /** Omit for a card with nothing to sell, e.g. a member whose admin cannot be asked. */
  cta?: {
    label: string;
    /** Standard seats get the bolt; Max and Practice seats get the Practice glyph. */
    icon: 'standard' | 'practice';
    onClick: () => void;
  };
  onCloseClick: () => void;
  ctaButtonRef?: Ref<HTMLButtonElement>;
  className?: string;
  'data-testid'?: string;
};

/** The sidebar usage card shown at 90% and 100%. */
export function UsageNudgeCard({
  title,
  body,
  learnMoreHref,
  cta,
  onCloseClick,
  ctaButtonRef,
  className,
  'data-testid': testId = 'usage-nudge-card',
}: UsageNudgeCardProps) {
  const intl = useIntl();
  const CtaIcon = cta?.icon === 'practice' ? TierPracticeFilled : TierProFilled;

  return (
    <div
      role="region"
      aria-label={title}
      data-testid={testId}
      className={cn(
        'flex w-[240px] max-w-[calc(100vw-2rem)] flex-col gap-3 p-3',
        className
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <TypographyP2SemiBold className="min-w-0 text-text-primary">
            {title}
          </TypographyP2SemiBold>
          <Button
            type="button"
            variant="ghost"
            size="xxs"
            iconOnly
            className="size-5 shrink-0 text-text-secondary"
            aria-label={intl.formatMessage(usageNudgeCardMessages.close)}
            onClick={onCloseClick}
            data-testid={`${testId}-close`}
          >
            <X className="size-4" aria-hidden />
          </Button>
        </div>
        <TypographyCaption className="text-text-primary">
          {body}
          {learnMoreHref ? (
            <>
              {' '}
              <Link
                variant="inline"
                href={learnMoreHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-primary decoration-text-primary/50 hover:text-text-primary hover:decoration-text-primary"
              >
                {intl.formatMessage(usageNudgeCardMessages.learnMore)}
              </Link>
            </>
          ) : null}
        </TypographyCaption>
      </div>

      {cta ? (
        <Button
          ref={ctaButtonRef}
          type="button"
          variant="secondary"
          size="md"
          className={cn('w-full gap-x-1.5', CLINICIAN_MAX_CREDIT_POPOVER_BUTTON_CLASS)}
          onClick={cta.onClick}
          data-testid={`${testId}-cta`}
        >
          <CtaIcon size={14} className="shrink-0" aria-hidden />
          {cta.label}
        </Button>
      ) : null}
    </div>
  );
}
